import { defer } from 'lodash'
import { type FileExtension, fileExtensionFromMimeType } from '../../../shared/domain/files/fileExtensions.no-deps'
import type { FileId } from '../../../shared/domain/files/types.no-deps'
import { createFileName } from '../../../shared/domain/flows/types.no-deps'
import type { UploadQueue } from './UploadQueue'
import { createFileUploadData } from './FileUploadData'
import type { UploadGroupId } from './Upload'
import type { CommonDeps } from '../base/CommonDeps'
import { readAsBuffer } from '../file/read'
import { type Buff, arrayBuffer } from './buffer'

export interface BlobSaveAndUpload {
    fileId: FileId
    blob: Blob
    deps: CommonDeps
    acceptedExtensions: FileExtension[]
    sizeLimitInBytes?: number
    groupId?: UploadGroupId
    writeArrayBufferToDisk?: (fileId: FileId, buffer: Buff) => Promise<void>
    uploadQueue: UploadQueue
    customer: number
}

/**
 * Save a blob file to disk and then upload
 * If write method not provided, just upload
 */
export async function blobSaveAndUpload(args: BlobSaveAndUpload) {
    const { blob } = args
    if (blob.type == null || blob.type === '') throw new Error('Blob has no mimetype')

    const fileName = createFileName(args.fileId, fileExtensionFromMimeType(blob.type))
    const fileUploadData = createFileUploadData(args.fileId, fileName, blob.size, blob.type)
    await args.uploadQueue.addUpload(
        fileUploadData,
        args.acceptedExtensions,
        args.sizeLimitInBytes,
        args.customer,
        args.groupId
    )

    if (args.writeArrayBufferToDisk != null) {
        // Do not hold up UI whilst saving to disk
        defer(async () => {
            const logger = args.deps.createLogger('blobSaveAndUpload')
            try {
                const buffer = await readAsBuffer(blob)
                await args.writeArrayBufferToDisk(args.fileId, arrayBuffer(buffer))
                await args.uploadQueue.writeToDiskComplete(args.fileId, logger)
            } catch (error) {
                args.deps.reportError(error)
                await args.uploadQueue.failedToWriteToDisk(args.fileId, logger)
            }
        })
    }

    return fileUploadData
}
